import React, { useContext, useEffect, useState } from 'react';
import { Icon, Input, Select } from '@app/components';
import {
  ActivityLogInfo,
  defaultSelectOption,
  defaultUserOption,
  ProUserInfo,
  UserBriefInfo,
  UserTypeOptions,
} from '@app/types';
import { useGetUsersbyKeywordQuery } from '@app/graphql';
import {
  ButtonText,
  ErrorMessage,
  InputBox,
  InputCard,
  Loader,
  PrimaryInfoBox,
  PrimaryInputCard,
  ProDropDown,
  ProfessionalUserType,
  ProUserInput,
  ProUserName,
} from '@app/modules/transaction-form/transactions-form-components';
import { Label } from '@app/components/input/input-components';
import { AuthContext } from '@app/providers';

type Props = {
  register: any;
  errors: any;
  isSubmitted: boolean;
  professionalUsers: UserTypeOptions[];
  setProfessionalUsers: (proUsers: UserTypeOptions[]) => void;
  regenerateChecklistAssignUsers: (proUsers: UserTypeOptions[]) => void;
  transactionId: string;
  profUserOptions: UserTypeOptions[];
  activityLog: ActivityLogInfo[];
};

const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;

export default function ProfessionalUsers({
  register,
  errors,
  isSubmitted,
  professionalUsers,
  setProfessionalUsers,
  regenerateChecklistAssignUsers,
  transactionId,
  profUserOptions,
  activityLog,
}: Props) {
  const [getUsers] = useGetUsersbyKeywordQuery({
    search: '',
  });
  const { userInfo }: any = useContext(AuthContext);
  const [userName, setUserName] = useState({ index: 0, useName: '' });
  const proDrodownRef: any = React.useRef(null);
  const [showDropdown, setShowProDropdown] = useState({
    show: false,
    index: 0,
  });
  const [proUsers, setProUsers] = useState<UserTypeOptions[]>(
    professionalUsers,
  );
  const [profOptions, setProfOptions] = useState<UserTypeOptions[]>([]);
  const [userLoading, setUserLoading] = useState(false);
  const [options, setOptions] = useState<ProUserInfo[]>([]);

  useEffect(() => {
    if (professionalUsers && professionalUsers.length) {
      if (!transactionId) {
        setProUsers(
          professionalUsers.filter((proUser: UserTypeOptions) => {
            return (
              proUser.key !== 'attorney_buy' && proUser.key !== 'attorney_sell'
            );
          }),
        );
      } else {
        setProUsers(professionalUsers);
      }
    }
  }, [professionalUsers, transactionId]);

  useEffect(() => {
    if (profUserOptions && profUserOptions.length) {
      setProfOptions(profUserOptions);
    }
  }, [profUserOptions]);

  /**
   *
   * @param e onchange event
   */
  function handleClickOutside(e: any) {
    if (
      showDropdown.show &&
      proDrodownRef.current &&
      !proDrodownRef.current.contains(e.target)
    ) {
      setShowProDropdown({ show: false, index: 0 });
    }
  }

  document.addEventListener('click', handleClickOutside);

  /**
   *
   * @param email email to validate
   * @returns whether email is valid or not
   */
  const validateEmail = (email: string) => {
    if (!emailPattern.test(email)) {
      return true;
    }
    return false;
  };

  const onChange = (e: any, type: string, i: number) => {
    let users: UserTypeOptions[] = [...proUsers];
    if (type === 'userType') {
      users[i] = {
        ...users[i],
        key: e.key,
        value: e.value,
        label: e.label,
        user: e.user,
        _id: e._id,
        profUserTypeId: e._id,
      };
    }
    setProUsers(users);
    setProfessionalUsers(users);
  };

  /**
   *
   * @param event onchange event
   * @param index index of the changed professional users
   */
  async function onProfessionalUserChange(event: any, index: number) {
    if (event && !/^\s+$/.test(event.target.value)) {
      if (event && !event.target.value) {
        handleRemoveProName(index);
        setShowProDropdown({ show: true, index: index });
      }
      let users = [...proUsers];
      users[index].user = { ...users[index].user, name: event.target.value };
      setUserName({ useName: event.target.value, index: index });
      setShowProDropdown({ show: true, index: index });
      setUserLoading(true);
      const searchUsers: any = await getUserByKey(event.target.value);
      if (searchUsers && searchUsers.length) {
        setOptions(searchUsers);
      }
      setProUsers(users);
    }
  }

  /**
   *
   * @param search parameter required to search users
   * @returns filtered search users
   */
  async function getUserByKey(search: string) {
    return await getUsers({ variables: { search: search } })
      .then((response: any) => {
        if (response && response.data) {
          const { getUsersByKeyword } = response.data;
          if (
            getUsersByKeyword &&
            getUsersByKeyword.success &&
            getUsersByKeyword.user
          ) {
            const {
              getUsersByKeyword: { user },
            } = response.data;

            let Users: any[] = [];
            Users = user.map((data: any) => {
              return {
                label:
                  data.company !== null
                    ? `${data.firstName} ${data.lastName} -  ${data.company}`
                    : `${data.firstName} ${data.lastName}`,

                value: data.firebaseId,
                ...data,
              };
            });
            setUserLoading(false);
            return Users;
          }
          setUserLoading(false);
          return [];
        }
        setUserLoading(false);
        return [];
      })
      .catch((error: any) => {
        setUserLoading(false);
        console.log(error);
      });
  }

  /**
   *
   * @param e on change event
   * @param key key of the changed input element
   * @param index index of the changed input element
   */
  const handleProUserInputChange = (e: any, key: string, index: number) => {
    let value = '';
    if (!/^\s+$/.test(key === 'phone' ? e : e.target.value)) {
      if (key === 'phone') {
        value = e;
      } else {
        value = e.target.value;
      }
      const list: UserTypeOptions[] = [...proUsers];
      let singleUser: UserBriefInfo = list[index].user;
      if (key === 'phone') {
        singleUser = { ...singleUser, phone: value };
      } else if (key === 'company') {
        singleUser = { ...singleUser, company: value };
      } else if (key === 'email') {
        singleUser = { ...singleUser, email: value };
      }
      list[index].user = singleUser;
      setProUsers(list);
      regenerateChecklistAssignUsers(list);
    }
  };

  const handleAddUser = () => {
    let users: UserTypeOptions[] = [...proUsers];
    users = users.concat({
      _id: '',
      profUserTypeId: '',
      label: '',
      value: '',
      user: defaultUserOption,
      key: '',
    });
    setProUsers(users);
    setProfessionalUsers(users);
  };

  /**
   *
   * @param userName selected name of user
   * @param userObject selected user object
   * @param index index of the selected user
   */
  const handleProUserSelect = (
    userName: string,
    userObject: any,
    index: number,
  ) => {
    const proUserListMod: UserTypeOptions[] = [...proUsers];
    proUserListMod[index].user = {
      name: userName.split('-')[0],
      company: userObject.company || '',
      email: userObject.email || '',
      phone: userObject.phone || '',
      address: userObject.address || '',
      new: false,
      firebaseId: userObject.firebaseId || '',
    };
    let email: any = document.getElementById(`proEmail-${index}`);
    let name: any = document.getElementById(`proName-${index}`);
    if (email) {
      email.value = userObject.email || '';
    }
    if (name) {
      name.value = userName ? userName.split('-')[0] : '';
    }
    if (transactionId) {
      activityLog.push({
        transactionId: transactionId,
        firebaseId: userInfo.firebaseId,
        item: 'Professional Users',
        description: `has been updated by `,
      });
    }
    setProUsers(proUserListMod);
    setProfessionalUsers(proUserListMod);
    regenerateChecklistAssignUsers(proUserListMod);
    setShowProDropdown({ show: false, index: 0 });
  };

  const handleRemoveProName = (i: number) => {
    const proUserListMod: UserTypeOptions[] = [...proUsers];
    proUserListMod[i].user = {
      name: '',
      company: '',
      email: '',
      phone: '',
      new: true,
    };
    setProUsers(proUserListMod);
    regenerateChecklistAssignUsers(proUserListMod);
  };

  return (
    <>
      {proUsers && proUsers.length ? (
        <>
          <div>
            {proUsers.map((userType: UserTypeOptions, i: number) => {
              return (
                <div
                  key={i}
                  style={{ position: 'relative', marginBottom: '24px' }}>
                  <ProfessionalUserType>
                    <Label>User Type</Label>
                    <Select
                      value={userType.value ? userType : defaultSelectOption}
                      options={profOptions}
                      onChange={(e: any) => onChange(e, 'userType', i)}
                    />
                  </ProfessionalUserType>
                  <InputBox>
                    <ProUserInput>
                      <div>
                        <Input
                          label="Name"
                          id={`proName-${i}`}
                          value={
                            userType.user && userType.user.name
                              ? userType.user.name
                              : ''
                          }
                          placeholder="Please Enter Name"
                          name={`proUserName-${i}`}
                          onChange={(e: any) => onProfessionalUserChange(e, i)}
                        />
                        {showDropdown.show && showDropdown.index === i ? (
                          <ProDropDown ref={proDrodownRef}>
                            {userLoading ? (
                              <Loader>
                                <Icon name="loader" />
                              </Loader>
                            ) : null}
                            {!userLoading && options && options.length
                              ? options.map((pU: any) => {
                                  return (
                                    <ProUserName
                                      onClick={() =>
                                        handleProUserSelect(pU.label, pU, i)
                                      }>
                                      {pU.label}
                                    </ProUserName>
                                  );
                                })
                              : null}
                            <span
                              style={{
                                color: 'red',
                                cursor: 'pointer',
                              }}
                              onClick={() =>
                                handleProUserSelect(userName.useName, {}, i)
                              }>
                              + Add New User:{' '}
                              <strong style={{ color: 'rgb(81,81,81)' }}>
                                {userName.useName}
                              </strong>
                            </span>
                          </ProDropDown>
                        ) : null}
                      </div>
                    </ProUserInput>
                    {proUsers[i].user && !proUsers[i].user.new ? (
                      <>
                        <InputCard>
                          <Input
                            disabled={
                              proUsers[i].user.firebaseId ? true : false
                            }
                            label="Company"
                            placeholder="Enter Company Name"
                            name="proCompany"
                            value={
                              proUsers[i].user.company
                                ? proUsers[i].user.company
                                : ''
                            }
                            onChange={e =>
                              handleProUserInputChange(e, 'company', i)
                            }
                            register={register({})}
                          />
                        </InputCard>

                        <InputCard>
                          <Input
                            id={`proEmail-${i}`}
                            type="email"
                            label="Email"
                            // star="*"
                            showDefaultValue={true}
                            placeholder="Enter Email"
                            name="proEmail"
                            value={
                              proUsers[i].user.email
                                ? proUsers[i].user.email
                                : ''
                            }
                            onChange={e =>
                              handleProUserInputChange(e, 'email', i)
                            }
                            register={register({
                              pattern: emailPattern,
                            })}
                          />
                          {errors.proEmail &&
                            errors.proEmail.type === 'pattern' &&
                            validateEmail(
                              proUsers[i].user.email
                                ? proUsers[i].user.email
                                : '',
                            ) && (
                              <ErrorMessage>
                                Please enter valid email
                              </ErrorMessage>
                            )}
                        </InputCard>
                      </>
                    ) : null}
                  </InputBox>
                  {userType.user && !userType.user.new ? (
                    <PrimaryInfoBox>
                      <PrimaryInputCard>
                        <Input
                          type="phone_number"
                          label="Phone Number"
                          placeholder="Enter Phone Number"
                          name="proNumber"
                          value={
                            (userType.user.phone &&
                            userType.user.phone.startsWith('+1')
                              ? userType.user.phone.substring(2)
                              : userType.user.phone) || ''
                          }
                          onChange={e =>
                            handleProUserInputChange(e, 'phone', i)
                          }
                          register={register({})}
                        />
                      </PrimaryInputCard>
                    </PrimaryInfoBox>
                  ) : null}
                </div>
              );
            })}
          </div>
          <ButtonText onClick={() => handleAddUser()}>+ Add User</ButtonText>
        </>
      ) : null}
    </>
  );
}
